import { Controller } from '@hotwired/stimulus'

// 補足情報の「全文を表示 / 閉じる」。PC/SP の component_controller はこのクラスを継承して使う。
export default class extends Controller {
  static targets = ['body', 'toggleOpen', 'toggleClose']

  get className () {
    return this.element.dataset.classname
  }

  connect () {
    // 省略の有無は行の高さに依存するため、Webフォント適用前に判定すると
    // 「省略されているのにトグルが出ない（残りが読めない）」側に倒れる。
    // fonts.ready 後に判定し、以後の折返し変化にも追従させる。
    const evaluate = () => this.updateToggleVisibility()

    if (document.fonts?.ready) {
      document.fonts.ready.then(evaluate)
    } else {
      requestAnimationFrame(evaluate)
    }

    // ResizeObserver は Safari 13.0 以前（macOS 10.15 / iOS 13.0〜13.3）に存在しない。
    // 未対応環境でそのまま new すると connect() が ReferenceError で中断し、
    // 折返し変化への追従が効かないうえに Sentry にも積まれる。
    // 初回判定は上の fonts.ready 側で走るため、ここでは追従だけを諦める。
    if (!window.ResizeObserver) { return }

    this.resizeObserver = new ResizeObserver(evaluate)
    this.resizeObserver.observe(this.bodyTarget)
  }

  disconnect () {
    this.resizeObserver?.disconnect()
  }

  // 4行に満たない補足情報では省略が起きず、押しても見た目が変わらないためトグル自体を出さない。
  // サーバ描画時点では hidden で出しておき、省略されている場合だけここで外す
  // （可視で描画するとフォント適用前の一瞬だけ出てから消える）。
  // 展開中は clientHeight が伸びて省略なしと同じ判定になるため触らない。
  updateToggleVisibility () {
    // fonts.ready の then は disconnect 後にも解決しうる（DOM差し替えで要素が外れたあと）。
    // ガードが無いと target 参照で Missing target element が投げられる
    if (!this.hasBodyTarget || !this.hasToggleOpenTarget) return
    if (this.isOpen) return

    // 1px の許容差を置く。行ボックスの高さは line-height 1.6 × 13px = 20.8px のような小数になり、
    // scrollHeight / clientHeight の整数への丸め方が揃わないと、省略が起きていないのに
    // 1px差で「省略あり」に倒れる（＝押しても何も変わらないトグルが出る）。
    // 1px未満の差でトグルを出す実益は無いため、差が1pxを超えたときだけ省略ありとみなす。
    const clamped = this.bodyTarget.scrollHeight - this.bodyTarget.clientHeight > 1
    this.toggleOpenTarget.classList.toggle(`${this.className}::hidden`, !clamped)
    // 判定が「走ったこと」と結果を要素に残す。
    // 省略なし側はトグルが hidden のままで、サーバ描画の初期状態と見分けが付かないため、
    // これが無いとJSが動いていない状態を「省略が起きていない」と誤って観測できてしまう
    // （system spec の待ち合わせにも使う）
    this.element.dataset.clamped = clamped
  }

  get isOpen () {
    return this.bodyTarget.classList.contains(`${this.className}::is-open`)
  }

  open () {
    this.bodyTarget.classList.add(`${this.className}::is-open`)
    this.toggleOpenTarget.classList.add(`${this.className}::hidden`)
    this.toggleCloseTarget.classList.remove(`${this.className}::hidden`)
    // 押したボタンが display:none になるためフォーカスが body に飛ぶ。対になるボタンへ移す
    this.toggleCloseTarget.focus()
  }

  close () {
    this.bodyTarget.classList.remove(`${this.className}::is-open`)
    this.toggleOpenTarget.classList.remove(`${this.className}::hidden`)
    this.toggleCloseTarget.classList.add(`${this.className}::hidden`)
    // 開いている間に横幅が変わってクランプしなくなった場合、無条件に出すと
    // 「押しても何も起きない全文を表示ボタン」が残るため、閉じた高さで再判定する
    this.updateToggleVisibility()

    // 押したボタンが display:none になるためフォーカスが body に飛ぶ。対になるボタンへ移す。
    // 上の再判定でそのボタン自体が消えることもあるので、残っているときだけ移す
    if (!this.toggleOpenTarget.classList.contains(`${this.className}::hidden`)) this.toggleOpenTarget.focus()
  }
}
